import { Avatar, Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";

function ViewChurchMember() {
  const { id } = useParams();
  const [memberDetails, setMemberDetails] = useState(null);

  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        const response = await authFetch(`/user/getUserById/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch church member details");
        }
        const memberData = await response.json();

        // Extract data from the API response
        const {
          email,
          profileName,
          streetNumber,
          streetName,
          APT,
          city,
          state,
          country,
          zipCode,
          imageUrl,
          Role,
          createdAt,
          Seller
        } = memberData.result;

        // Construct the memberDetails object with proper data
        const formattedMemberDetails = {
          email,
          profileName,
          streetNumber,
          streetName,
          APT,
          city,
          state,
          country,
          zipCode,
          imageUrl,
          role: Role?.role || "No role assigned", // Safe access to role
          createdAt,
          // Seller specific information
          sellerName: Seller?.sellername,
          orgName: Seller?.orgname,
          sellerPhone: Seller?.sellerphone,
          businessType: Seller?.bussinesstype,
          deliverType: Seller?.delivertype,
          accountNumber: Seller?.accountnumber,
          description: Seller?.description,
          paymentMethod: Seller?.paymentmethod,
          // Categories
          categories: Seller?.SellerProductCategories?.map(item => 
            item.Category?.name
          ).filter(Boolean) || []
        };

        setMemberDetails(formattedMemberDetails);
      } catch (error) {
        console.error("Error fetching church member details:", error);
      }
    };

    fetchMemberDetails();
  }, [id]);

  const renderDetails = () => {
    if (!memberDetails) return null;

    const basicDetails = [
      { label: "Profile Name", value: memberDetails.profileName },
      { label: "Email", value: memberDetails.email },
      { label: "Role", value: memberDetails.role },
    ];

    const addressDetails = [
      { label: "Street Number", value: memberDetails.streetNumber },
      { label: "Street Name", value: memberDetails.streetName },
      { label: "APT", value: memberDetails.APT },
      { label: "City", value: memberDetails.city },
      { label: "State", value: memberDetails.state },
      { label: "Country", value: memberDetails.country },
      { label: "Zip Code", value: memberDetails.zipCode },
    ];

    const sellerDetails = memberDetails.role === "seller" ? [
      { label: "Seller Name", value: memberDetails.sellerName },
      { label: "Organization Name", value: memberDetails.orgName },
      { label: "Phone", value: memberDetails.sellerPhone },
      { label: "Business Type", value: memberDetails.businessType },
      { label: "Delivery Type", value: memberDetails.deliverType },
      { label: "Account Number", value: memberDetails.accountNumber },
      { label: "Description", value: memberDetails.description },
      { label: "Payment Method", value: memberDetails.paymentMethod },
      { 
        label: "Categories", 
        value: memberDetails.categories.length > 0 
          ? memberDetails.categories.join(", ") 
          : "No categories" 
      },
    ] : [];

    const systemDetails = [
      { label: "Created At", value: formatCreatedAt(memberDetails.createdAt) },
    ];

    const allDetails = [
      ...basicDetails,
      ...addressDetails,
      ...sellerDetails,
      ...systemDetails
    ];

    return (
      <Grid container spacing={3} justifyContent="center">
        {/* Uncomment and fix the Avatar if you want to display the image */}
        {/* <Grid item xs={12} md={3}>
          <Avatar
            alt="Church Member"
            src={memberDetails.imageUrl !== "000000" ? `/public/assets/${memberDetails.imageUrl}` : "/default-avatar.png"}
            sx={{ width: 150, height: 150 }}
          />
        </Grid> */}
        <Grid item xs={12} md={9}>
          {allDetails.map((item) => (
            <Grid container spacing={1} key={item.label} sx={{ marginBottom: 1 }}>
              <Grid item xs={12} sm={4} md={3}>
                <Typography
                  variant="body1"
                  sx={{ 
                    fontSize: "1rem", 
                    fontWeight: "bold",
                    color: "text.secondary"
                  }}
                >
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={9}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: "1rem",
                    wordBreak: "break-word"
                  }}
                >
                  {item.value || "Not provided"}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  };

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "Not available";
    
    const date = new Date(createdAt);
    return date.toLocaleString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  return (
    <Box p="20px">
      <Header
        title={`View ${memberDetails?.role === "seller" ? "Seller" : "User"} ID ${id}`}
        subtitle={memberDetails ? `Role: ${memberDetails.role}` : "Loading..."}
      />
      <Box m="10px 0 0 0" height="55vh" overflow="auto">
        {memberDetails ? renderDetails() : <Typography>Loading...</Typography>}
      </Box>
    </Box>
  );
}

export default ViewChurchMember;